import React, { useState, useEffect } from 'react'
import InputMask from 'react-input-mask'
import { t } from 'i18n'
import axios from 'axios'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import camelCase from 'lodash/camelCase'
import snakeCase from 'lodash/snakeCase'
import moment from 'moment'
import { SignDocumentModal } from './SignDocumentModal'
import Alert from '../utilityComponents/Alert'
import RequiredMark, { RequiredFieldsLegend } from '../utilityComponents/RequiredMark'
import { notify } from '../../files/toasts'
import InputWrapper from '../utilityComponents/InputWrapper'

const scope = 'components.inheritance_claims'

const NATURAL_PERSON = 'natural_person'
const LEGAL_PERSON = 'legal_person'

const deepTransformObjectKeys = (object, transformFunction) => {
  if (!object) return object

  return Object.fromEntries(
    Object.entries(object).map(([key, value]) => {
      const valueIsObject = typeof value === 'object'
      const newValue = valueIsObject ? deepTransformObjectKeys(value, transformFunction) : value

      return [transformFunction(key), newValue]
    })
  )
}

const ClaimForm = ({
  newInheritanceClaimTemplate,
  inheritanceClaim,
  isEditing = false
}) => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [representationTypeOptions, setRepresentationTypeOptions] = useState([])
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false)
  const [claimSelectOptions, setClaimSelectOptions] = useState({
    claimType: [],
    claimJustification: []
  })

  const inheritanceClaimPersisted = !!inheritanceClaim?.id
  const formDisabled = !isEditing && inheritanceClaimPersisted

  const isDraft = inheritanceClaim?.status === 'DRAFT'
  const isSubmitted = inheritanceClaim?.status === 'SUBMITTED'

  const prefilledInheritanceClaim = inheritanceClaimPersisted
    ? inheritanceClaim
    : newInheritanceClaimTemplate

  const initialValues = {
    matterId: prefilledInheritanceClaim.matterId,
    creditorType: prefilledInheritanceClaim.creditorType,
    creditorsPersonalCode: prefilledInheritanceClaim.creditorsPersonalCode,
    creditorsFirstName: prefilledInheritanceClaim.creditorsFirstName,
    creditorsLastName: prefilledInheritanceClaim.creditorsLastName,
    creditorsRegistrationNumber: prefilledInheritanceClaim.creditorsRegistrationNumber,
    creditorsLegalName: prefilledInheritanceClaim.creditorsLegalName,
    creditorsResidentialAddress: prefilledInheritanceClaim.creditorsResidentialAddress,
    creditorsLegalAddress: prefilledInheritanceClaim.creditorsLegalAddress,
    creditorsEmail: prefilledInheritanceClaim.creditorsEmail,
    creditorsPhone: prefilledInheritanceClaim.creditorsPhone,
    documentNumber: prefilledInheritanceClaim.documentNumber,
    claimType: prefilledInheritanceClaim.claimType,
    claimJustification: prefilledInheritanceClaim.claimJustification,
    claimDynamicFields: deepTransformObjectKeys(prefilledInheritanceClaim.claimDynamicFields, camelCase),
    claimAmount: prefilledInheritanceClaim.claimAmount,
    description: prefilledInheritanceClaim.description,
    representationType: prefilledInheritanceClaim.representationType,
    termsConfirmed: (inheritanceClaimPersisted && !isEditing) ? true : false
  }

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    fetchClaimFields()
  }, [])

  const fetchClaimFields = () => {
    axios.get('/inheritance_claims/get_claim_fields').then(({ data }) => {
      const flattenedClaimTypes = flattenSelectOptionsWithDepth(data.claim_type)
      const flattenedClaimJustifications = flattenSelectOptionsWithDepth(data.claim_justification)

      setClaimSelectOptions({
        claimType: flattenedClaimTypes,
        claimJustification: flattenedClaimJustifications
      })

      setRepresentationTypeOptions(data.representations)
    }).catch(({ response }) => {
      console.log(response)
    })
  }

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target
    const fieldValue = type === 'checkbox' ? checked : value

    setFormValues({
      ...formValues,
      [name]: fieldValue
    })
  }

  const handleRepresentationChange = (value) => {
    setFormValues({
      ...formValues,
      representationType: value
    })
  }

  const handleSubmit = () => {
    setLoading(true)
    setErrors({})

    const editingClaimId = isEditing ? { id: inheritanceClaim.id } : {}

    const creditorTypeFields = {
      natural_person: {
        creditors_personal_code: formValues.creditorsPersonalCode,
        creditors_first_name: formValues.creditorsFirstName,
        creditors_last_name: formValues.creditorsLastName,
        creditors_residential_address: formValues.creditorsResidentialAddress
      },
      legal_person: {
        creditors_registration_number: formValues.creditorsRegistrationNumber,
        creditors_legal_name: formValues.creditorsLegalName,
        creditors_legal_address: formValues.creditorsLegalAddress
      }
    }[formValues.creditorType]

    const inheritanceClaimFields = {
      ...editingClaimId,
      ...creditorTypeFields,
      matter_id: formValues.matterId,
      document_number: formValues.documentNumber,
      claim_type: formValues.claimType,
      claim_justification: formValues.claimJustification,
      claim_amount: formValues.claimAmount,
      description: formValues.description,
      terms_confirmed: formValues.termsConfirmed,
      creditor_type: formValues.creditorType,
      creditors_email: formValues.creditorsEmail,
      creditors_phone: formValues.creditorsPhone,
      representation_type: formValues.representationType,
      claim_dynamic_fields: deepTransformObjectKeys(formValues.claimDynamicFields, snakeCase)
    }

    axios.post('/inheritance_claims/submit_claim.json', {
      inheritance_claim: inheritanceClaimFields
    }).then(({ data }) => {
      location = `/inheritance_claims/${data.id}`
    }).catch(({ response }) => {
      setErrors(response.data)
      const baseErrors = response.data?.base
      const noticeText = baseErrors?.length
        ? baseErrors.join(' ')
        : t('project_submission_failed', { scope })
      notify({ text: noticeText, type: 'danger' })
    }).finally(() => {
      setLoading(false)
    })
  }

  const renderCreditorTypeOption = (type) => {
    return (
      <label className='me-4'>
        <input
          type='radio'
          className='me-2'
          name='creditorType'
          value={type}
          checked={formValues.creditorType === type}
          onChange={handleChange}
        />
        <span>{t(type, { scope })}</span>
      </label>
    )
  }

  // Place tree into single array with 'depth' property
  const flattenSelectOptionsWithDepth = (options, depth = 0, flattenedOptions = []) => {
    options.map(opt => {
      flattenedOptions.push({ ...opt, depth: depth })

      if (opt.children && opt.children.length > 0) {
        flattenSelectOptionsWithDepth(opt.children, depth + 1, flattenedOptions)
      }
    })

    return flattenedOptions
  }

  const handleClaimSelectChange = (claimField, code) => {
    const dynamicFields = {}
    claimSelectOptions[claimField].find(opt => opt.code === code)?.fields?.forEach(field => {
      dynamicFields[field.name] = ''
    })

    setFormValues({
      ...formValues,
      [claimField]: code,
      claimDynamicFields: {
        ...formValues.claimDynamicFields,
        [claimField]: dynamicFields
      }
    })
  }

  const renderClaimSelect = (field) => {
    const options = claimSelectOptions[field]
    const selectedOption = options.find(opt => opt.code === formValues[field])

    const snakeCaseField = snakeCase(field)

    return (
      <InputWrapper errorArray={errors[snakeCaseField]} label={t(snakeCaseField, { scope })} htmlFor={field} required>
        <Select
          classNamePrefix='react-select'
          className={`react-select ${field}`}
          inputId={field}
          name={field}
          options={options}
          isOptionDisabled={opt => !opt.selectable}
          getOptionLabel={opt => opt.name}
          getOptionValue={opt => opt.code}
          styles={{ option: (styles, { data }) => {
            return { ...styles, paddingLeft: `${14 + data.depth * 20}px` }
          }}}
          clearable={false}
          value={selectedOption}
          placeholder={t(`prompts.${snakeCaseField}`, { scope })}
          onChange={({ code }) => handleClaimSelectChange(field, code)}
          isDisabled={formDisabled}
        />
      </InputWrapper>
    )
  }

  const getClaimDynamicFields = (claimSelectField) => {
    const selectedOption = claimSelectOptions[claimSelectField].find(opt =>
      opt.code === formValues[claimSelectField]
    )

    return selectedOption?.fields || []
  }

  const dynamicFieldId = (claimSelectField, field) => `${claimSelectField}_${field.name}`

  const renderDynamicField = (scope, field) => {
    const DEFAULT_MAX_LENGTH = 4000 // NIS default varchar limit
    const { name, type, maxLength } = field
    const id = dynamicFieldId(scope, field)

    const handleDynamicFieldChange = (value) => {
      setFormValues({
        ...formValues,
        claimDynamicFields: {
          ...formValues.claimDynamicFields,
          [scope]: {
            ...formValues.claimDynamicFields[scope],
            [name]: value
          }
        }
      })
    }

    const fieldValue = formValues.claimDynamicFields[scope][name]

    switch(type) {
      case 'date': return (
        <DatePicker
          className='form-control'
          id={id}
          name={name}
          selected={fieldValue ? moment(fieldValue) : null}
          onChange={date => handleDynamicFieldChange(date.format('YYYY-MM-DD'))}
        />
      )
      case 'string':
        return <>
          <input
            type='text'
            className='form-control'
            id={id}
            name={name}
            value={fieldValue || ''}
            maxLength={maxLength}
            onChange={({ target: { value } }) => handleDynamicFieldChange(value)}
          />
          {maxLength && maxLength < DEFAULT_MAX_LENGTH &&
            <div className='form-text text-end'>
              {`${fieldValue?.length || 0}/${maxLength}`}
            </div>
          }
        </>
      case 'number': return (
        <input
          type='number'
          className='form-control'
          id={id}
          name={name}
          value={fieldValue || ''}
          onChange={({ target: { value } }) => handleDynamicFieldChange(value)}
        />
      )
    }
  }

  const renderRepresentationTypeRadioButtons = () => {
    return (
      <InputWrapper errorArray={errors.representation_type}>
        <div>
          {representationTypeOptions.map(option => {
            return (
              <label className='me-4 mb-0' key={option.code}>
                <input
                  type='radio'
                  className='me-2'
                  name='representationType'
                  value={option.code}
                  checked={formValues.representationType === option.code}
                  onChange={() => handleRepresentationChange(option.code)}
                />
                <span>{option.name}</span>
              </label>
            )
          })}
        </div>
      </InputWrapper>
    )
  }

  const renderInputField = (name, { showLabel = true, required = true } = {}) => {
    const snakeCaseField = snakeCase(name)

    return (
      <InputWrapper errorArray={errors[snakeCaseField]} label={showLabel && t(snakeCaseField, { scope })} htmlFor={name} required={required}>
        <input
          type='text'
          className='form-control'
          id={name}
          name={name}
          placeholder={inheritanceClaimPersisted ? '-' : t(`prompts.${snakeCaseField}`, { scope })}
          value={formValues[name]}
          onChange={handleChange}
        />
      </InputWrapper>
    )
  }

  const renderNaturalPersonInputs = () => {
    return (
      <>
        <InputWrapper errorArray={errors.creditors_personal_code} label={t('creditors_personal_code', { scope })} htmlFor='creditorsPersonalCode' required>
          <InputMask
            type='text'
            className='form-control'
            id='creditorsPersonalCode'
            name='creditorsPersonalCode'
            placeholder={t('prompts.creditors_personal_code', { scope })}
            value={formValues.creditorsPersonalCode}
            mask={'999999-99999'}
            onChange={handleChange}
          />
        </InputWrapper>
        <div className='row'>
          <div className='col-md-6'>
            {renderInputField('creditorsFirstName')}
          </div>
          <div className='col-md-6'>
            {renderInputField('creditorsLastName')}
          </div>
        </div>
        {renderInputField('creditorsResidentialAddress')}
      </>
    )
  }

  const renderLegalPersonInputs = () => {
    return (
      <>
        <InputWrapper errorArray={errors.creditors_registration_number} label={t('creditors_registration_number', { scope })} htmlFor='creditorsRegistrationNumber' required>
          <InputMask
            type='text'
            className='form-control'
            id='creditorsRegistrationNumber'
            name='creditorsRegistrationNumber'
            placeholder={t('prompts.creditors_registration_number', { scope })}
            value={formValues.creditorsRegistrationNumber}
            mask={'99999999999'}
            onChange={handleChange}
          />
        </InputWrapper>
        {renderInputField('creditorsLegalName')}
        {renderInputField('creditorsLegalAddress')}
      </>
    )
  }

  const renderSignableDocumentButtons = () => {
    return <>
      <a
        className='btn btn-outline btn-lg mt-3 mb-4 me-3'
        href={`/inheritance_claims/${inheritanceClaim.id}/view_claim_document`}
        target='_blank'
        disabled={!formValues.termsConfirmed}
      >
        {t('preview_document', { scope })}
      </a>
      <button
        className='btn btn-primary btn-lg mt-3 mb-4'
        onClick={() => setShowSignDocumentModal(true)}
        disabled={!formValues.termsConfirmed || isSubmitted}
      >
        {isSubmitted
          ? t('document_submitted', { scope })
          : t('sign_and_submit_document', { scope })
        }
      </button>
    </>
  }

  const renderSaveProjectButton = () => {
    return (
      <button
        className='btn btn-primary btn-lg mt-3 mb-4'
        onClick={handleSubmit}
        disabled={!formValues.termsConfirmed || loading}
      >
        {t('save_project', { scope })}
      </button>
    )
  }

  const renderNoticeSection = () => {
    if (isDraft) {
      return <Alert type='info'>{t('notice.project_saved', { scope })}</Alert>
    } else if (isEditing) {
      return <Alert type='info'>{t('notice.editing', { scope })}</Alert>
    } else if (isSubmitted) {
      return <Alert type='success'>{t('notice.project_submitted', { scope })}</Alert>
    }
  }

  return (
    <div className='service-form'>
      {renderNoticeSection()}
      <fieldset disabled={formDisabled}>
        <h2 className='section-title'>{t('creditor', { scope })}</h2>
        <div className='mb-2'>
          {renderCreditorTypeOption(NATURAL_PERSON)}
          {renderCreditorTypeOption(LEGAL_PERSON)}
        </div>

        {formValues.creditorType === NATURAL_PERSON &&
          renderNaturalPersonInputs()
        }

        {formValues.creditorType === LEGAL_PERSON &&
          renderLegalPersonInputs()
        }

        {renderInputField('creditorsEmail')}
        {renderInputField('creditorsPhone')}

        <h2 className='section-title'>{t('claim', { scope })}</h2>

        {renderInputField('documentNumber', { required: false })}

        {renderClaimSelect('claimType')}
        {getClaimDynamicFields('claimType').map(field =>
          <InputWrapper errorArray={errors[snakeCase(field.name)]} key={field.name} label={field.title} htmlFor={dynamicFieldId('claimType', field)} required={field.required}>
            {renderDynamicField('claimType', field)}
          </InputWrapper>
        )}

        {renderClaimSelect('claimJustification')}
        {getClaimDynamicFields('claimJustification').map(field =>
          <InputWrapper errorArray={errors[snakeCase(field.name)]} key={field.name} label={field.title} htmlFor={dynamicFieldId('claimJustification', field)} required={field.required}>
            {renderDynamicField('claimJustification', field)}
          </InputWrapper>
        )}

        {renderInputField('claimAmount')}

        <InputWrapper errorArray={errors.description} label={t('description', { scope })} htmlFor='description'>
          <textarea
            rows='3'
            id='description'
            name='description'
            className='form-control'
            value={formValues.description}
            onChange={handleChange}
            maxLength={600}
          />
          <div className='form-text text-end'>
            {`${formValues.description?.length || 0}/600`}
          </div>
        </InputWrapper>

        <fieldset className='mb-4'>
          <legend className='form-label'>
            {t('representation', { scope })} <RequiredMark />
          </legend>

          {renderRepresentationTypeRadioButtons()}
        </fieldset>

        <div className='mt-1 mb-3 lh-base informative-block'>
          <span className='text-danger me-1'>*</span>
          <span
            className='semi-opaque'
            dangerouslySetInnerHTML={{__html: t('criminal_law_text_html', { scope }) }}
          ></span>
        </div>

        <div className='mt-4 mb-3 lh-base'>
          <label>
            <input
              type='checkbox'
              className='me-2'
              name='termsConfirmed'
              checked={formValues.termsConfirmed}
              onChange={handleChange}
            />
            <RequiredMark />{' '}
            <span>{t('terms_confirmed', { scope })}</span>
          </label>
        </div>
      </fieldset>

      <RequiredFieldsLegend />
      <div className='text-center mt-4'>
        {inheritanceClaimPersisted && !isEditing &&
          renderSignableDocumentButtons()
        }
        {(!inheritanceClaimPersisted || isEditing) &&
          renderSaveProjectButton()
        }
        {showSignDocumentModal &&
          <SignDocumentModal
            inheritanceClaim={inheritanceClaim}
            onClose={() => setShowSignDocumentModal(false)}
          />
        }
      </div>
    </div>
  )
}

export { ClaimForm }
